'use client';

import { ArrowUpRight, Calendar, Clock } from 'lucide-react';

const ARTICLES = [
  {
    id: 'art1',
    title: 'L\'art du tannage végétal : 42 jours de patience',
    excerpt: "Dans nos ateliers d'Anvers et de Dakar, le tannage végétal est un rituel. Quarante-deux jours pendant lesquels le cuir tremille dans les bains d'écorce, de châtaignier et de mimosa. Découvrez pourquoi cette lenteur est notre plus grande richesse.",
    category: 'Savoir-faire',
    date: '2024-12-15',
    readTime: '6 min',
    image: '/images/atelier.png',
  },
  {
    id: 'art2',
    title: 'Pourquoi un sac à 1 240 € peut être un investissement',
    excerpt: "Un sac Signature Cognac coûte 1 240 €. Somme folle ou placement raisonné ? Nous démontons les coûts : cuir pleine fleur, 36 heures de main-d'œuvre, certification, garantie à vie, réparabilité. Un sac qui dure cent ans coûte moins cher qu'un sac qui dure cinq ans.",
    category: 'Philosophie',
    date: '2024-12-08',
    readTime: '8 min',
    image: '/images/bag-signature.png',
  },
  {
    id: 'art3',
    title: 'Dakar ↔ Anvers : la double vie d\'un cuir panafricain',
    excerpt: "Comment un même sac peut-il naître sur deux continents ? Récit d'un savoir-faire partagé entre la lumière ocre de l'île de Gorée et les brumes de la Meir. Quand la diaspora devient méthode créative.",
    category: 'Héritage',
    date: '2024-11-30',
    readTime: '10 min',
    image: '/images/heritage-panafricain.png',
  },
  {
    id: 'art4',
    title: 'La patine : quand le cuir vieillit comme un grand cru',
    excerpt: "Pourquoi les amateurs disent qu'un cuir MATAR se bonifie avec le temps ? Parce que la cire d'abeille, les sept couches de patine, le polissage à la brosse de chèvre, créent une matière vivante. Suivez la vie d'un sac Signature sur dix ans.",
    category: 'Entretien',
    date: '2024-11-22',
    readTime: '5 min',
    image: '/images/leather-texture.png',
  },
];

export default function Blog() {
  return (
    <section
      id="chroniques"
      className="relative py-24 sm:py-32 bg-[#0a0a0f] border-t border-[#D4AF37]/10 overflow-hidden"
    >
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="block h-px w-10 bg-[#D4AF37]" />
            <span className="eyebrow">Chroniques de la Maison</span>
            <span className="block h-px w-10 bg-[#D4AF37]" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
            <span className="text-gold-gradient">Lectures</span>{' '}
            <span className="text-[#F4E8C1]">d'atelier.</span>
          </h2>
          <p className="max-w-2xl mx-auto text-[#F4E8C1]/65 font-light leading-relaxed">
            Le journal de la maison MATAR STYLE CUIR. Récits, savoir-faire,
            philosophie du cuir. Pour celles et ceux qui veulent comprendre
            avant d'acheter, et rêver avant de comprendre.
          </p>
        </div>

        {/* Featured article (first) */}
        <a
          href="#chroniques"
          className="group block relative overflow-hidden border border-[#D4AF37]/25 hover:border-[#D4AF37]/55 transition-colors mb-10"
        >
          <div className="grid lg:grid-cols-2 gap-0">
            {/* Image */}
            <div className="relative aspect-[16/10] lg:aspect-auto overflow-hidden">
              <img
                src={ARTICLES[0].image}
                alt={ARTICLES[0].title}
                className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                style={{ filter: 'brightness(0.7) saturate(1.15)' }}
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(90deg, transparent 0%, rgba(10,10,15,0.6) 100%)',
                }}
              />
              {/* Featured badge */}
              <div className="absolute top-4 left-4 bg-gold-gradient text-[#0a0a0f] text-[9px] uppercase tracking-[0.25em] font-bold px-3 py-1.5">
                À la une
              </div>
            </div>

            {/* Content */}
            <div className="p-7 sm:p-10 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-4 text-[10px] uppercase tracking-[0.25em]">
                <span className="text-[#D4AF37] font-semibold">{ARTICLES[0].category}</span>
                <span className="text-[#F4E8C1]/45">·</span>
                <span className="text-[#F4E8C1]/55 flex items-center gap-1.5">
                  <Calendar size={11} />
                  {new Date(ARTICLES[0].date).toLocaleDateString('fr-BE', { year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
                <span className="text-[#F4E8C1]/45">·</span>
                <span className="text-[#F4E8C1]/55 flex items-center gap-1.5">
                  <Clock size={11} />
                  {ARTICLES[0].readTime}
                </span>
              </div>

              <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-[#F4E8C1] group-hover:text-[#D4AF37] transition-colors mb-4 leading-tight">
                {ARTICLES[0].title}
              </h3>

              <p className="text-[#F4E8C1]/70 leading-relaxed font-light mb-6 text-sm sm:text-base">
                {ARTICLES[0].excerpt}
              </p>

              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
                Lire la chronique
                <ArrowUpRight size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </div>
            </div>
          </div>
        </a>

        {/* Grid of other articles */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {ARTICLES.slice(1).map((article) => (
            <a
              key={article.id}
              href="#chroniques"
              className="group bg-[#12121a]/60 backdrop-blur-sm border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-colors overflow-hidden flex flex-col"
            >
              {/* Image */}
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
                  style={{ filter: 'brightness(0.75) saturate(1.1)' }}
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      'linear-gradient(180deg, transparent 40%, rgba(10,10,15,0.85) 100%)',
                  }}
                />
                {/* Category badge */}
                <div className="absolute top-3 left-3 bg-[#0a0a0f]/80 backdrop-blur-sm border border-[#D4AF37]/30 text-[#D4AF37] text-[9px] uppercase tracking-[0.2em] font-semibold px-2.5 py-1">
                  {article.category}
                </div>
              </div>

              {/* Content */}
              <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center gap-2 text-[9px] uppercase tracking-[0.2em] text-[#F4E8C1]/55 mb-3">
                  <span className="flex items-center gap-1">
                    <Calendar size={10} />
                    {new Date(article.date).toLocaleDateString('fr-BE', { year: 'numeric', month: 'short', day: 'numeric' })}
                  </span>
                  <span>·</span>
                  <span className="flex items-center gap-1">
                    <Clock size={10} />
                    {article.readTime}
                  </span>
                </div>

                <h3 className="font-display text-base sm:text-lg font-semibold text-[#F4E8C1] group-hover:text-[#D4AF37] transition-colors mb-2 leading-tight">
                  {article.title}
                </h3>

                <p className="text-xs text-[#F4E8C1]/60 leading-relaxed font-light flex-1 line-clamp-3 mb-4">
                  {article.excerpt}
                </p>

                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
                  Lire
                  <ArrowUpRight size={11} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-14 text-center">
          <a
            href="#sur-invitation"
            className="inline-flex items-center justify-center gap-3 px-8 py-4 border border-[#D4AF37]/45 text-[#F4E8C1] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] transition-all duration-500 text-xs uppercase tracking-[0.2em] font-medium"
          >
            Recevoir les chroniques par email
            <ArrowUpRight size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}
